import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Box, Button, Center, Collapse, Flex, Stack, Tooltip, useDisclosure } from '@chakra-ui/react';
import { faChevronDown, faChevronRight, faPen } from '@fortawesome/pro-regular-svg-icons';

import { Icon, RoundBadgeWithTooltip } from 'shared-components';
import { ApplicationState } from '@/store';
import { getFulfilmentProp } from '@/utilities/fulfilmentData';
import {
  IApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskStatus,
  SettableTaskLevel,
  ApplicantOfferStatus,
  ApplicantStatusReason,
  DocumentType,
  ApplicantOfferRequirementTask,
  EventBookingStatus,
  IAcademicResultFulfilmentData,
  ICalendlyInterviewFulfilmentData,
  RequirementTaskInternalDataMethod,
  ResultStatus,
  getOfferScopedDocKey,
} from 'types';

import {
  updateApplicantOfferStatus,
  updateApplicantRequirementLevel,
  updateApplicantRequirementStatus,
} from '../../../../store/apply/actions';
import { BorderedRow } from '../../../atoms/bordered-row';
import { FulfilmentRendererFactory } from './requirement-rows/fulfilment-renderers';
import {
  doesRendererHandleTaskText,
  doesRendererSkipPrefix,
} from './requirement-rows/fulfilment-renderers/rendererConfig';
import { Note } from './Note';
import PreviousCondition from './PreviousCondition';
import CalendlyInterviewMenu from './CalendlyInterviewMenu';
import InternalDocumentUploadBtn from './InternalDocumentUploadBtn';
import { InternalDocumentUploadModal } from './InternalDocumentUploadModal';
import UploadIconBtn from './UploadIconBtn';
import { useInternalDocumentUpload } from '@/hooks/useInternalDocumentUpload';
import { StatusMenu } from './StatusMenu';
import { TaskTypeMenu } from './TaskTypeMenu';
import { EditTextFieldModal } from './modals/EditTextFieldModal';
import { RequirementNotSatisfiedModal } from './modals/RequirementNotSatisfiedModal';
import type { MultiFileUploadV2FileItem as FileItem } from 'shared-components';
import { useDocumentSave } from '../../../../hooks/useDocumentSave';
import { DocumentViewer } from '../../../organisms/DocumentViewer';
import { useTranslation } from "react-i18next";

type Props = {
  requirement: IApplicantOfferRequirementTask;
  userId: string;
  uni: string;
  offerId: string;
  index: number;
  requirementsConditionTexts: {
    [key in string]: string;
  };
  disableEdit?: boolean;
  hideUploadButton?: boolean;
  requirementText?: string;
  offerStatus?: ApplicantOfferStatus;
  applicationId?: string;
};

interface FulfilmentData {
  urls?: string[];
  sKeys?: string[];
  s3Keys?: string[];
}

type TextFieldModalConfig = {
  title: string;
  fieldLabel: string;
  fieldSubLabel?: string;
  placeholder?: string;
  initialValue: string;
  required?: boolean;
  singleLine?: boolean;
  confirmLabel?: string;
  onSave: (value: string) => void;
};

// Multi-requirement fallback: use task.type for lookups since task.id may be suffixed
// (e.g. "AcademicTranscript-abc123"). Falls back to task.id for legacy data.
export function getRequirementType(
  requirement: Pick<IApplicantOfferRequirementTask, 'id' | 'type'>,
): ApplicantOfferRequirementTask {
  return (requirement.type ?? requirement.id) as ApplicantOfferRequirementTask;
}

export function getRequirementDocumentConfig(
  requirement: Pick<IApplicantOfferRequirementTask, 'id' | 'type'>,
  offerId: string,
): { docType: DocumentType; docKey: string } {
  const requirementType = getRequirementType(requirement);
  let docType: DocumentType;

  if (requirementType === ApplicantOfferRequirementTask.LetterOfRecommendation) {
    docType = DocumentType.ReferenceLetter;
  } else if (requirementType === ApplicantOfferRequirementTask.EnglishLanguageCertificate) {
    docType = DocumentType.EnglishTest;
  } else if (requirementType === ApplicantOfferRequirementTask.PhotoID) {
    docType = DocumentType.PhotoId;
  } else {
    docType = DocumentType[requirementType];
  }

  let docKey = getOfferScopedDocKey(docType, offerId);

  if (requirementType === ApplicantOfferRequirementTask.Custom) {
    docKey = `${requirement.id}-${offerId}`;
    if (!docType) {
      docType = DocumentType.CustomRequirement;
    }
  } else if (requirement.type === ApplicantOfferRequirementTask.StaffOnly) {
    docKey = `${requirement.id}-${offerId}`;
    if (!docType) {
      docType = DocumentType.StaffOnlyRequirement;
    }
  }

  // Multi-requirement academic tasks have suffixed IDs (e.g. "AcademicTranscript-qualId123").
  // Use the full task ID in the docKey to prevent document collisions between
  // tasks of the same type belonging to different academic entry requirements.
  const isMultiRequirementTask = requirement.type && requirement.id !== requirement.type;
  if (isMultiRequirementTask && requirementType !== ApplicantOfferRequirementTask.Custom) {
    docKey = `${requirement.id}-${offerId}`;
  }

  return { docType, docKey };
}

/**
 * Checks whether the AcademicResults fulfilment data indicates predicted results.
 * Used to render a "Predicted" badge next to the assessment dropdown.
 * Only returns true for InterimResults (predicted) — not for NoResults (pending).
 * Accepts the effective (pending-aware) fulfilment data so the badge stays in sync
 * with the rest of the row when pendingChanges.fulfilmentData is present.
 */
export function hasPredictedResults(
  requirement: IApplicantOfferRequirementTask,
  effectiveFulfilmentData: IApplicantOfferRequirementTask['fulfilmentData'],
): boolean {
  if (requirement.type !== ApplicantOfferRequirementTask.AcademicResults) return false;
  const data = effectiveFulfilmentData as IAcademicResultFulfilmentData | undefined;
  if (!data?.studyHistory) return false;

  const { resultStatus, qualificationTypeSelections } = data.studyHistory;
  if (qualificationTypeSelections?.length) {
    return qualificationTypeSelections.some(sel => sel.resultStatus === ResultStatus.InterimResults);
  }
  return resultStatus === ResultStatus.InterimResults;
}

export default function RequirementRow({
  requirement: requirementData,
  userId,
  uni,
  offerId,
  index,
  requirementsConditionTexts,
  disableEdit = false,
  hideUploadButton = false,
  requirementText,
  offerStatus,
  applicationId,
}: Props): React.ReactElement {
    const { t } = useTranslation('common');
  const [requirement, setRequirement] = useState(requirementData);
  const internalUpload = useInternalDocumentUpload({ requirement, offerId });
  const { handleSaveDocument, handleDeleteDocument } = useDocumentSave();

  const { pendingChanges } = requirement;
  let { fulfilmentData } = requirement;
  if (pendingChanges.fulfilmentData !== undefined) {
    ({ fulfilmentData } = pendingChanges);
  }
  const application = useSelector((state: ApplicationState) => state.apply.application);

  const documentUrls = useSelector((state: ApplicationState) => state.apply.documentUrls);
  const { uploadedSuccessfully, fileUploadError, deletedSuccessfully } = useSelector(
    (state: ApplicationState) => state.student.documents,
  );
  const displayStatus = pendingChanges.status ?? requirement.status;
  const displayConditionText = pendingChanges.conditionText ?? requirement.conditionText;
  const [status, setStatus] = useState(displayStatus);
  const [conditionText, setConditionText] = useState(displayConditionText);
  const [noteText, setNoteText] = useState(requirement.internalNote);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>();

  const [textFieldModal, setTextFieldModal] = useState<TextFieldModalConfig | null>(null);
  const closeTextFieldModal = (): void => setTextFieldModal(null);

  const dispatch = useDispatch();
  const {
    isOpen: historyIsOpen,
    onOpen: historyOnOpen,
    onClose: historyOnClose,
    onToggle: historyOnToggle,
  } = useDisclosure();
  const [isReRequestingCondition, setIsReRequestingCondition] = useState(false);
  const [showNotSatisfiedModal, setShowNotSatisfiedModal] = useState(false);

  const { docType, docKey } = getRequirementDocumentConfig(requirement, offerId);

  useEffect(() => {
    setRequirement(requirementData);
    setIsReRequestingCondition(false);
  }, [requirementData]);

  useEffect(() => {
    setStatus(pendingChanges.status ?? requirement.status);
  }, [requirement.status, pendingChanges.status]);

  useEffect(() => {
    setConditionText(pendingChanges.conditionText ?? requirement.conditionText);
  }, [requirement.conditionText, pendingChanges.conditionText]);

  useEffect(() => {
    setNoteText(requirement.internalNote);
  }, [requirement.internalNote]);

  useEffect(() => {
    // reset when re-requesting condition is opened but user changes the status to something else (user may not understand he needs to click Cancel, so he just changes the status)
    if (status !== ApplicantOfferRequirementTaskStatus.Condition && isReRequestingCondition) {
      setIsReRequestingCondition(false);
      setRequirement(requirementData);
      historyOnClose();
    }
  }, [status, isReRequestingCondition, requirementData]);

  const handleStatusUpdate = useCallback(
    (statusToUpdate: ApplicantOfferRequirementTaskStatus, newNoteText?: string, newConditionText?: string) => {
      dispatch(
        updateApplicantRequirementStatus({
          applicantId: userId,
          uni,
          offerId,
          id: requirement.id,
          status: statusToUpdate,
          noteText: newNoteText,
          conditionText: [
            ApplicantOfferRequirementTaskStatus.Condition,
            ApplicantOfferRequirementTaskStatus.Assess,
          ].includes(statusToUpdate)
            ? newConditionText
            : undefined,
        }),
      );
    },
    [offerId, dispatch, uni, userId, requirement.id],
  );

  const handleLevelChange = useCallback(
    (level: SettableTaskLevel) => {
      dispatch(
        updateApplicantRequirementLevel({
          applicantId: userId,
          uni,
          offerId,
          id: requirement.id,
          level,
        }),
      );
    },
    [offerId, dispatch, uni, userId, requirement.id],
  );

  const handleReRequest = useCallback(
    (description: string, internalNote: string) => {
      handleStatusUpdate(ApplicantOfferRequirementTaskStatus.Condition, internalNote, description);
      setShowNotSatisfiedModal(false);
    },
    [handleStatusUpdate],
  );

  const handleWithdrawFromNotSatisfied = useCallback(
    (reason: ApplicantStatusReason, explanation: string, note: string, notify: boolean) => {
      handleStatusUpdate(ApplicantOfferRequirementTaskStatus.NotSatisfied, note);
      if (applicationId) {
        dispatch(
          updateApplicantOfferStatus({
            applicantId: userId,
            uni,
            offerId,
            applicationId,
            status: ApplicantOfferStatus.Withdrawn,
            statusReason: reason,
            statusExplanationToApplicant: explanation,
            statusInternalNote: note,
            notifyApplicant: notify,
          }),
        );
      }
      setShowNotSatisfiedModal(false);
    },
    [handleStatusUpdate, dispatch, userId, uni, offerId, applicationId],
  );

  const isNoteEnabled = (statusToCheck: ApplicantOfferRequirementTaskStatus, isApplicantUpload: boolean): boolean => {
    if (statusToCheck === ApplicantOfferRequirementTaskStatus.Assess && isApplicantUpload) return true;
    return [
      ApplicantOfferRequirementTaskStatus.Satisfied,
      ApplicantOfferRequirementTaskStatus.Provisional,
      ApplicantOfferRequirementTaskStatus.Condition,
      ApplicantOfferRequirementTaskStatus.Exempt,
      ApplicantOfferRequirementTaskStatus.NotSatisfied,
    ].includes(statusToCheck);
  };

  const openFile = useCallback((urls: string[]): void => {
    setPreviewUrls(urls);
    setIsPreviewOpen(true);
  }, []);

  const handleStatusChange = (newStatus: ApplicantOfferRequirementTaskStatus): void => {
    // Conditions can be reselected to select again
    if (newStatus === status) return;

    if (newStatus === ApplicantOfferRequirementTaskStatus.NotSatisfied) {
      setShowNotSatisfiedModal(true);
      return;
    }

    if (newStatus === ApplicantOfferRequirementTaskStatus.Exempt) {
      setTextFieldModal({
        title: 'Task exempt',
        fieldLabel: 'Add an internal note (optional)',
        fieldSubLabel: 'Not shared with the applicant',
        placeholder: 'Add note...',
        initialValue: '',
        confirmLabel: 'Confirm exemption',
        onSave: (internalNote: string) => {
          setNoteText(internalNote);
          setStatus(ApplicantOfferRequirementTaskStatus.Exempt);
          handleStatusUpdate(ApplicantOfferRequirementTaskStatus.Exempt, internalNote);
          closeTextFieldModal();
        },
      });
      return;
    }

    let newConditionText = '';
    if (newStatus === ApplicantOfferRequirementTaskStatus.Condition) {
      // Use task type for lookup since requirementsConditionTexts is keyed by the task type enum,
      // and task IDs may be suffixed for multi-requirement academic tasks (e.g. AcademicResults-abc123).
      newConditionText = requirementsConditionTexts?.[requirement.type] ?? requirement.conditionText;
    }

    // If the requirement is set back to what it previously was then pre-fill with the saved note
    let initialNoteValue = '';
    if (requirement.pendingChanges.status === newStatus) {
      initialNoteValue = requirement.internalNote ?? '';
    }

    if (newStatus === ApplicantOfferRequirementTaskStatus.Condition && getFulfilmentProp(fulfilmentData, 'urls')) {
      // Re-request condition flow: reorganise history, show history section.
      // PreviousCondition handles its own note entry via initialEditMode.
      setConditionText(newConditionText);
      setStatus(newStatus);
      const newRequirement = structuredClone(requirementData);

      newRequirement.conditionHistory.unshift({
        fulfilmentData: newRequirement.fulfilmentData,
        conditionText: newRequirement.conditionText,
        internalNote: '',
      });

      newRequirement.pendingChanges.fulfilmentData = null;
      setRequirement(newRequirement);
      setIsReRequestingCondition(true);
      historyOnOpen();
    } else if (!isNoteEnabled(newStatus, requirement.lastUpdatedByApplicant)) {
      handleStatusUpdate(newStatus);
    } else if (newStatus === ApplicantOfferRequirementTaskStatus.Condition) {
      setTextFieldModal({
        title: 'Set condition',
        fieldLabel: 'Description',
        fieldSubLabel: 'Shared with the applicant',
        placeholder: 'Add description...',
        initialValue: newConditionText,
        onSave: (description: string) => {
          setConditionText(description);
          setStatus(newStatus);
          handleStatusUpdate(newStatus, undefined, description);
          closeTextFieldModal();
        },
      });
    } else {
      setTextFieldModal({
        title:
          newStatus === ApplicantOfferRequirementTaskStatus.Satisfied
            ? 'Mark as satisfied'
            : 'Mark as provisionally satisfied',
        fieldLabel: 'Internal note (optional)',
        fieldSubLabel: 'Not shared with the applicant',
        placeholder: 'Add note...',
        initialValue: initialNoteValue,
        onSave: (note: string) => {
          setNoteText(note);
          setStatus(newStatus);
          handleStatusUpdate(newStatus, note);
          closeTextFieldModal();
        },
      });
    }
  };

  const handleUpload = (files: FileItem[], deletedS3Keys?: string[]): void => {
    const newFileNames: string[] = [];
    const newS3Keys: string[] = [];

    for (const file of files) {
      const fileWithMeta = file as FileItem & { url?: string; s3Key?: string };
      if (fileWithMeta.url) {
        newFileNames.push(file.name);
        const derivedKey = fileWithMeta.s3Key || fileWithMeta.url.split('/').slice(3).join('/').split('?')[0];
        if (derivedKey) newS3Keys.push(derivedKey);
      }
    }

    const toDelete = deletedS3Keys ?? [];
    const existingS3Keys = (fulfilmentDataTyped?.s3Keys || []).filter(k => !toDelete.includes(k));
    const existingFileNames = existingS3Keys.map(k => k.split('/').pop() || k);

    const fileNames = [...existingFileNames, ...newFileNames];
    const s3Keys = [...existingS3Keys, ...newS3Keys];

    if (fileNames.length > 0) {
      handleSaveDocument({
        id: requirement.id,
        docType,
        docKey,
        fileNames,
        s3Keys,
        uploadedBy: userId,
      });
    }
  };

  const fulfilmentDataTyped = fulfilmentData as FulfilmentData;

  const handleDeleteExistingFile = useCallback(
    async (s3KeyToDelete: string) => {
      handleDeleteDocument({
        requirementId: requirement.id,
        docType,
        docKey,
        s3Key: s3KeyToDelete,
        sKey: getFulfilmentProp(fulfilmentData, 'sKey') as string,
      });
    },
    [requirement.id, docType, docKey, fulfilmentData, documentUrls, handleDeleteDocument],
  );

  const shouldHideTaskText = requirementText && requirement?.taskText === requirementText;
  const hasPendingChanges = Object.keys(pendingChanges ?? {}).length > 0;

  // Multi-requirement academic tasks have suffixed IDs (e.g., "AcademicResults-qualId123").
  // This controls the display format for academic results: detailed for single, compact for multi.
  const isMultiQualification = Boolean(requirement.type && requirement.id !== requirement.type);

  const hasFulfilmentData = Object.keys(fulfilmentData ?? {}).length > 0;
  const isOfferIssued = offerStatus !== undefined && offerStatus !== ApplicantOfferStatus.Applied;

  const isCalendlyInterview = requirement.type === ApplicantOfferRequirementTask.CalendlyInterview;
  const rendererShowsTaskText = doesRendererHandleTaskText(requirement.type);
  const calendlyFulfilmentData = isCalendlyInterview
    ? (fulfilmentData as ICalendlyInterviewFulfilmentData | null)
    : null;
  const isCalendlyBooked = calendlyFulfilmentData?.bookingStatus === EventBookingStatus.BOOKED;
  const hasInternalDataMethod = requirement.internalDataMethod === RequirementTaskInternalDataMethod.Upload;
  const requirementPrefix =
    !hasFulfilmentData && !hideUploadButton && !doesRendererSkipPrefix(requirement.type) ? 'Provide ' : '';
  const shouldHideUploadButton = hasInternalDataMethod || hideUploadButton;

  return (
    <BorderedRow>
      <Flex alignItems="start" gap={2}>
        {requirement.lastUpdatedByApplicant && (
          <Tooltip hasArrow placement="left" label={t('common:new')}>
            <Center position={'absolute'} left={2} top={5} width={4} height={4}>
              <Box
                width={2}
                height={2}
                borderRadius={'full'}
                bgColor={'indigo.500'}
                _dark={{ bgColor: 'indigo.300' }}
              />
            </Center>
          </Tooltip>
        )}

        <Box fontSize={'lg'} lineHeight={6} flexGrow={1} wordBreak="break-word">
          {shouldHideTaskText ? (
            <FulfilmentRendererFactory
              fulfilmentData={fulfilmentData}
              requirementId={requirement.id}
              taskType={requirement.type}
              application={application}
              openFile={openFile}
              taskText={requirement.taskText}
              isMultiQualification={isMultiQualification}
              internalData={requirement.internalData}
            />
          ) : (
            <>
              {requirement?.taskText && !rendererShowsTaskText && (
                <Box>{`${requirementPrefix}${requirement.taskText}`}</Box>
              )}
              <FulfilmentRendererFactory
                fulfilmentData={fulfilmentData}
                requirementId={requirement.id}
                taskType={requirement.type}
                application={application}
                openFile={openFile}
                taskText={requirement.taskText}
                isMultiQualification={isMultiQualification}
                internalData={requirement.internalData}
              />
            </>
          )}
        </Box>

        {!disableEdit && (
          <Flex alignItems="center" gap={2} flexShrink={0} my={-1}>
            {hasPendingChanges && (
              <RoundBadgeWithTooltip icon={faPen} label={t('common:hasPendingChanges')} colorScheme="orange" size="xs" />
            )}

            {hasPredictedResults(requirement, fulfilmentData) && (
              <Badge colorScheme="gray" variant="solid" fontSize="xs" px={2} py={0.5} borderRadius="md">
                {t('common:predicted')}
                                            </Badge>
            )}

            {hasFulfilmentData || status === ApplicantOfferRequirementTaskStatus.Exempt ? (
              <StatusMenu
                status={status}
                isDisabled={disableEdit}
                fulfilmentData={fulfilmentData}
                onStatusChange={handleStatusChange}
              />
            ) : (
              <TaskTypeMenu
                currentLevel={pendingChanges.level ?? requirement.level}
                isDisabled={disableEdit}
                isOfferIssued={isOfferIssued}
                onLevelChange={handleLevelChange}
                onExempt={() => {
                  setTextFieldModal({
                    title: 'Task exempt',
                    fieldLabel: 'Add an internal note (optional)',
                    fieldSubLabel: 'Not shared with the applicant',
                    placeholder: 'Add note...',
                    initialValue: '',
                    confirmLabel: 'Confirm exemption',
                    onSave: (internalNote: string) => {
                      setNoteText(internalNote);
                      setStatus(ApplicantOfferRequirementTaskStatus.Exempt);
                      handleStatusUpdate(ApplicantOfferRequirementTaskStatus.Exempt, internalNote);
                      closeTextFieldModal();
                    },
                  });
                }}
              />
            )}
            <Flex gap={1} flexShrink={0} minW="32px">
              {isCalendlyInterview && (
                <>
                  {hasInternalDataMethod && (
                    <InternalDocumentUploadModal
                      requirement={requirement}
                      isOpen={internalUpload.isOpen}
                      onClose={internalUpload.onClose}
                      docKey={internalUpload.docKey}
                      onProceed={internalUpload.onProceed}
                      uploadedSuccessfully={internalUpload.uploadedSuccessfully}
                      fileUploadError={internalUpload.fileUploadError}
                      existingFiles={internalUpload.existingFiles}
                    />
                  )}
                  <CalendlyInterviewMenu
                    requirement={requirement}
                    uni={uni}
                    offerId={offerId}
                    applicantId={userId}
                    isBooked={isCalendlyBooked}
                    isOfferIssued={isOfferIssued}
                    utmKey={calendlyFulfilmentData?.utmKey ?? ''}
                    startDate={calendlyFulfilmentData?.startDate}
                    joinUrl={calendlyFulfilmentData?.joinUrl}
                    onUpload={hasInternalDataMethod ? internalUpload.onOpen : undefined}
                  />
                </>
              )}
              {!isCalendlyInterview && hasInternalDataMethod && (
                <InternalDocumentUploadBtn requirement={requirement} offerId={offerId} />
              )}
              {!shouldHideUploadButton && (
                <UploadIconBtn
                  onProceed={handleUpload}
                  onDeleteExistingFile={handleDeleteExistingFile}
                  hide={hideUploadButton}
                  uploadedSuccessfully={uploadedSuccessfully}
                  fileUploadError={fileUploadError}
                  deletedSuccessfully={deletedSuccessfully}
                  documentConfig={{
                    docType,
                    docKey,
                    label: requirement.taskText,
                  }}
                  existingFiles={fulfilmentDataTyped}
                />
              )}
            </Flex>
          </Flex>
        )}
      </Flex>

      {isNoteEnabled(status, requirement.lastUpdatedByApplicant) && (
        <Note
          status={status}
          level={pendingChanges.level ?? requirement.level}
          noteText={
            [ApplicantOfferRequirementTaskStatus.Condition, ApplicantOfferRequirementTaskStatus.Assess].includes(status)
              ? conditionText
              : noteText
          }
          onEdit={() => {
            if (status === ApplicantOfferRequirementTaskStatus.Exempt) {
              setTextFieldModal({
                title: 'Edit exempt reason',
                fieldLabel: 'Exempt reason',
                fieldSubLabel: 'Not shared with the applicant',
                placeholder: 'Add note...',
                initialValue: noteText ?? '',
                onSave: (internalNote: string) => {
                  setNoteText(internalNote);
                  handleStatusUpdate(ApplicantOfferRequirementTaskStatus.Exempt, internalNote);
                  closeTextFieldModal();
                },
              });
            } else if (
              [ApplicantOfferRequirementTaskStatus.Condition, ApplicantOfferRequirementTaskStatus.Assess].includes(
                status,
              )
            ) {
              setTextFieldModal({
                title: 'Edit task description',
                fieldLabel: 'Description',
                fieldSubLabel: 'Shared with the applicant',
                placeholder: 'Add description...',
                initialValue: conditionText ?? '',
                onSave: (newDescription: string) => {
                  setConditionText(newDescription);
                  handleStatusUpdate(status, noteText, newDescription);
                  closeTextFieldModal();
                },
              });
            } else {
              setTextFieldModal({
                title: 'Edit internal note',
                fieldLabel: 'Internal note',
                fieldSubLabel: 'Not shared with the applicant',
                placeholder: 'Add note...',
                initialValue: noteText ?? '',
                onSave: (note: string) => {
                  setNoteText(note);
                  handleStatusUpdate(status, note);
                  closeTextFieldModal();
                },
              });
            }
          }}
          id={requirement.id}
          index={index}
          disableEdit={requirement.lastUpdatedByApplicant || disableEdit}
        />
      )}

      {requirement.conditionHistory?.length > 0 && (
        <Box>
          <Button
            onClick={historyOnToggle}
            variant={'link'}
            colorScheme={'gray'}
            size={'xs'}
            leftIcon={<Icon icon={historyIsOpen ? faChevronDown : faChevronRight} fixedWidth />}
            lineHeight={4}
            sx={{
              '.chakra-button__icon': {
                marginInlineEnd: '.125rem', // Intentionally override chakra theme to allow tighter icon spacing
              },
            }}
          >
            {t('common:previous')}
                                </Button>

          <Collapse in={historyIsOpen} animateOpacity>
            <Stack spacing={4}>
              {requirement.conditionHistory.map((oldCondition, oldConditionIndex) => {
                return (
                  <PreviousCondition
                    requirementId={requirement.id}
                    taskType={requirement.type}
                    application={application}
                    taskText={requirement.taskText}
                    isMultiQualification={isMultiQualification}
                    data={oldCondition}
                    key={oldConditionIndex}
                    index={oldConditionIndex}
                    openFile={urls => {
                      openFile(urls);
                    }}
                    handleStatusUpdate={handleStatusUpdate}
                    status={status}
                    conditionText={conditionText}
                    // for now we only allow edit note for the latest condition
                    initialEditMode={isReRequestingCondition && oldConditionIndex === 0}
                    disableEdit={
                      ApplicantOfferRequirementTaskStatus.Condition !== status || oldConditionIndex !== 0 || disableEdit
                    }
                    onEditCancel={() => {
                      // Reset stuff like they were before re-requesting condition
                      setRequirement(requirementData);
                      setIsReRequestingCondition(false);
                      setStatus(requirementData.pendingChanges.status ?? requirementData.status);
                      historyOnClose();
                    }}
                  />
                );
              })}
            </Stack>
          </Collapse>
        </Box>
      )}

      {isPreviewOpen && <DocumentViewer documents={previewUrls} onClose={() => setIsPreviewOpen(false)} />}

      {textFieldModal && (
        <EditTextFieldModal
          isOpen={true}
          onClose={closeTextFieldModal}
          title={textFieldModal.title}
          fieldLabel={textFieldModal.fieldLabel}
          fieldSubLabel={textFieldModal.fieldSubLabel}
          placeholder={textFieldModal.placeholder}
          initialValue={textFieldModal.initialValue}
          required={textFieldModal.required}
          singleLine={textFieldModal.singleLine}
          confirmLabel={textFieldModal.confirmLabel}
          onSave={textFieldModal.onSave}
        />
      )}

      <RequirementNotSatisfiedModal
        isOpen={showNotSatisfiedModal}
        onClose={() => setShowNotSatisfiedModal(false)}
        onReRequest={handleReRequest}
        onWithdraw={handleWithdrawFromNotSatisfied}
        defaultDescription={requirementsConditionTexts?.[requirement.type] ?? requirement.conditionText}
      />
    </BorderedRow>
  );
}
